import { useCallback, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Accept } from 'react-dropzone';
import PrimitiveFileInput from '@/components/form/_primitives/PrimitiveFileInput.tsx';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import InputHint from '@/components/form/base/InputHint.tsx';
import { FileAction } from '@/schemas/fileFieldSchema.ts';

interface SinglePrimitiveFileInputProps {
  fieldName: string;
  accept?: Accept;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  removable?: boolean;
  error?: string;
  hint?: string;
  className?: string;
}

const SingleFileInput = ({
  fieldName,
  accept,
  label,
  required,
  disabled,
  removable = true,
  error,
  hint,
  className,
}: SinglePrimitiveFileInputProps) => {
  const { getValues, setValue, watch } = useFormContext();

  const hadPreviousFile = useRef(!!getValues(`${fieldName}.fileId`));

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return;

      const file = acceptedFiles[0];
      setValue(`${fieldName}.file`, file, { shouldDirty: true });
      setValue(`${fieldName}.fileName`, file.name);
      setValue(
        `${fieldName}.action`,
        hadPreviousFile.current ? FileAction.UPDATE : FileAction.CREATE,
      );
    },
    [fieldName, setValue],
  );

  const handleRemove = useCallback(() => {
    if (hadPreviousFile.current) {
      setValue(`${fieldName}.action`, FileAction.DELETE, { shouldDirty: true });
      setValue(`${fieldName}.file`, undefined);
      setValue(`${fieldName}.fileName`, undefined);
      setValue(`${fieldName}.fileUrl`, undefined);
    } else {
      setValue(`${fieldName}.action`, FileAction.NO_ACTION, {
        shouldDirty: true,
      });
      setValue(`${fieldName}.file`, undefined);
      setValue(`${fieldName}.fileName`, undefined);
    }
  }, [fieldName, setValue]);

  const file: File | undefined = watch(`${fieldName}.file`);
  const fileUrl: string | undefined = watch(`${fieldName}.fileUrl`);
  const fileName: string | undefined = watch(`${fieldName}.fileName`);
  const action: FileAction = watch(`${fieldName}.action`);

  const showFileUrl = action !== FileAction.DELETE ? fileUrl : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <InputLabel label={label} required={required} disabled={disabled} />
      )}
      <PrimitiveFileInput
        onDrop={handleDrop}
        accept={accept}
        disabled={disabled}
        file={file}
        fileUrl={showFileUrl}
        fileName={fileName}
        onRemove={removable ? handleRemove : undefined}
        className={className}
      />
      <InputHint hint={hint} />
      <InputError error={error} />
    </div>
  );
};

SingleFileInput.displayName = 'SinglePrimitiveFileInput';

export default SingleFileInput;
